import { Badge } from '@crowdex/ui/components/badge';
import { Button } from '@crowdex/ui/components/button';
import { Checkbox } from '@crowdex/ui/components/checkbox';
import { DesktopIconButton, DesktopNavButton } from '@crowdex/ui/components/desktop';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@crowdex/ui/components/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@crowdex/ui/components/dropdown-menu';
import { EmptyState, InlineMessage, LoadingState } from '@crowdex/ui/components/feedback';
import { Field } from '@crowdex/ui/components/field';
import { Input } from '@crowdex/ui/components/input';
import {
  ActionBar,
  Panel,
  PanelBody,
  PanelHeader,
  PreferenceRow,
} from '@crowdex/ui/components/panel';
import { PendingButton } from '@crowdex/ui/components/pending-button';
import { Popover, PopoverContent, PopoverTrigger } from '@crowdex/ui/components/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@crowdex/ui/components/select';
import { Switch } from '@crowdex/ui/components/switch';
import { Textarea } from '@crowdex/ui/components/textarea';
import { toast } from '@crowdex/ui/components/toaster';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@crowdex/ui/components/tooltip';
import { Folder, MoreHorizontal, Plus, Settings } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { CharterBoard } from '@/components/design-system-charter';
import { PluginFrameError } from '@/features/plugins/plugin-frame-error';

const SWATCHES = [
  ['Background', 'bg-background'],
  ['Card', 'bg-card'],
  ['Accent', 'bg-accent'],
  ['Sunken', 'bg-surface-sunken'],
  ['Panel', 'bg-surface-panel'],
  ['Raised', 'bg-surface-raised'],
  ['Overlay', 'bg-surface-overlay'],
  ['Solid', 'bg-surface-solid'],
] as const;

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <Panel>
      <PanelHeader title={title} description={description} />
      <PanelBody className="space-y-4">{children}</PanelBody>
    </Panel>
  );
}

function DemoSelect({ id, ...props }: { id?: string; 'aria-describedby'?: string }) {
  return (
    <Select defaultValue="personal">
      <SelectTrigger id={id} aria-label="Workspace" {...props} className="w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="personal">Personal workspace</SelectItem>
        <SelectItem value="team">Team workspace</SelectItem>
        <SelectItem value="disabled" disabled>
          Unavailable workspace
        </SelectItem>
      </SelectContent>
    </Select>
  );
}

function OverlayExamples() {
  return (
    <ActionBar className="justify-start">
      <Dialog>
        <DialogTrigger asChild>
          <Button variant="outline">Open dialog</Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Project preferences</DialogTitle>
            <DialogDescription>
              Use Tab to navigate, Escape to close, and check that focus returns to the trigger.
            </DialogDescription>
          </DialogHeader>
          <Field id="dialog-project" label="Project name">
            {InputForField}
          </Field>
          <Field id="dialog-workspace" label="Workspace">
            {(control) => <DemoSelect {...control} />}
          </Field>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline">Popover inside dialog</Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-4">
              <p className="text-sm">This surface inherits the theme and stays above its dialog.</p>
            </PopoverContent>
          </Popover>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <DialogClose asChild>
              <Button
                onClick={() =>
                  toast.success('Preferences saved', 'Demo only — no data is persisted.')
                }
              >
                Save preferences
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline">
            <MoreHorizontal />
            Open menu
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem onSelect={() => toast.info('Open project selected')}>
            Open project
          </DropdownMenuItem>
          <DropdownMenuItem disabled>Unavailable action</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DesktopIconButton aria-label="Gallery settings">
            <Settings />
          </DesktopIconButton>
        </TooltipTrigger>
        <TooltipContent>Settings tooltip</TooltipContent>
      </Tooltip>
      <Button
        variant="ghost"
        onClick={() =>
          toast.error(
            'Example error',
            'A long explanation should wrap without hiding the dismiss button.',
          )
        }
      >
        Show notification
      </Button>
    </ActionBar>
  );
}

// A render function keeps Field's label/description contract explicit.
const InputForField = (props: import('@crowdex/ui/components/field').FieldControlProps) => (
  <Input {...props} defaultValue="Crowdex" />
);

/** `?theme=light` opens the reference on the light theme (screenshots of both). */
function initialTheme(): 'light' | 'dark' {
  return new URLSearchParams(window.location.search).get('theme') === 'light' ? 'light' : 'dark';
}

export function DesktopDesignSystemPreview() {
  const [theme, setTheme] = useState<'light' | 'dark'>(initialTheme);
  const [compact, setCompact] = useState(false);
  const [narrow, setNarrow] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    const body = document.body;
    const html = document.documentElement;
    const previous = {
      theme: body.dataset['theme'],
      compact: body.dataset['compact'],
      motion: body.dataset['reducedMotion'],
      light: html.classList.contains('light'),
      dark: html.classList.contains('dark'),
      scheme: html.style.colorScheme,
    };
    return () => {
      for (const [key, value] of [
        ['theme', previous.theme],
        ['compact', previous.compact],
        ['reducedMotion', previous.motion],
      ] as const) {
        if (value === undefined) delete body.dataset[key];
        else body.dataset[key] = value;
      }
      html.classList.toggle('light', previous.light);
      html.classList.toggle('dark', previous.dark);
      html.style.colorScheme = previous.scheme;
    };
  }, []);
  useEffect(() => {
    document.body.dataset['theme'] = `desktop-${theme}`;
    document.body.dataset['compact'] = String(compact);
    document.body.dataset['reducedMotion'] = String(reducedMotion);
    document.documentElement.classList.toggle('light', theme === 'light');
    document.documentElement.classList.toggle('dark', theme === 'dark');
    document.documentElement.style.colorScheme = theme;
  }, [theme, compact, reducedMotion]);

  return (
    <TooltipProvider>
      <main
        className="h-dvh overflow-y-auto bg-background p-4 text-foreground sm:px-12 sm:py-10"
        data-slot="ui-gallery"
      >
        <div className="mx-auto max-w-[84rem] space-y-6">
          <CharterBoard theme={theme} />
          <header className="space-y-2 pt-6">
            <h2 className="type-card-title">Crowdex UI reference</h2>
            <p className="max-w-2xl text-sm text-muted-foreground">
              Live shared components, states and compositions. Appearance changes apply to this
              preview only.
            </p>
          </header>
          <Panel>
            <PanelBody className="flex flex-wrap items-end gap-4">
              <Field id="gallery-theme" label="Color scheme">
                {(control) => (
                  <Select
                    value={theme}
                    onValueChange={(value) => setTheme(value === 'light' ? 'light' : 'dark')}
                  >
                    <SelectTrigger {...control} className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dark">Dark</SelectItem>
                      <SelectItem value="light">Light</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <label htmlFor="gallery-compact" className="flex items-center gap-2 text-sm">
                <Switch id="gallery-compact" checked={compact} onCheckedChange={setCompact} />
                Compact
              </label>
              <label htmlFor="gallery-motion" className="flex items-center gap-2 text-sm">
                <Switch
                  id="gallery-motion"
                  checked={reducedMotion}
                  onCheckedChange={setReducedMotion}
                />
                Reduce motion
              </label>
              <Button variant="outline" aria-pressed={narrow} onClick={() => setNarrow(!narrow)}>
                {narrow ? 'Use full width' : 'Constrain content width'}
              </Button>
            </PanelBody>
          </Panel>
          <div className={`mx-auto grid min-w-0 gap-6 ${narrow ? 'max-w-sm' : 'lg:grid-cols-2'}`}>
            <Section
              title="Buttons and badges"
              description="One filled cobalt button per zone; the others are bordered, on the control surface or bare."
            >
              <ActionBar className="justify-start">
                {(['default', 'secondary', 'outline', 'ghost', 'destructive', 'link'] as const).map(
                  (variant) => (
                    <Button key={variant} variant={variant}>
                      {variant}
                    </Button>
                  ),
                )}
              </ActionBar>
              <ActionBar className="justify-start">
                {(['xs', 'sm', 'default', 'lg'] as const).map((size) => (
                  <Button key={size} size={size} variant="outline">
                    {size}
                  </Button>
                ))}
                <Button size="icon" aria-label="Add item">
                  <Plus />
                </Button>
              </ActionBar>
              <ActionBar className="justify-start">
                <Button disabled>Disabled</Button>
                <PendingButton pending pendingLabel="Saving…">
                  Save
                </PendingButton>
              </ActionBar>
              <ActionBar className="justify-start">
                {(
                  ['default', 'outline', 'attention', 'running', 'success', 'destructive'] as const
                ).map((variant) => (
                  <Badge key={variant} variant={variant}>
                    {variant}
                  </Badge>
                ))}
                <Badge mono>main</Badge>
              </ActionBar>
              <Button variant="outline" className="h-auto max-w-full whitespace-normal text-left">
                A long action label wraps onto multiple lines without hiding its purpose
              </Button>
            </Section>
            <Section
              title="Form controls"
              description="Labels, help text, invalid and disabled states."
            >
              <Field
                id="gallery-name"
                label="Name"
                description="A description linked to the input."
              >
                {(control) => <Input {...control} placeholder="Project name" />}
              </Field>
              <Field
                id="gallery-error"
                label="Email"
                error="Enter a valid email address before continuing."
              >
                {(control) => <Input {...control} defaultValue="invalid-address" type="email" />}
              </Field>
              <Field id="gallery-disabled" label="Read-only workspace">
                {(control) => <Input {...control} value="Managed by your organization" disabled />}
              </Field>
              <Field id="gallery-select" label="Workspace">
                {(control) => <DemoSelect {...control} />}
              </Field>
              <Field id="gallery-notes" label="Notes">
                {(control) => <Textarea {...control} placeholder="Add a longer description…" />}
              </Field>
              <label htmlFor="gallery-archived" className="flex items-center gap-2 text-sm">
                <Checkbox
                  id="gallery-archived"
                  checked={checked}
                  onCheckedChange={(value) => setChecked(value === true)}
                />
                Include archived projects
              </label>
            </Section>
            <Section
              title="Compositions"
              description="Reusable preference rows, panel headers and action bars."
            >
              <PreferenceRow
                title="Desktop notifications"
                description="Notify me when an agent needs attention."
              >
                <Switch
                  aria-label="Desktop notifications"
                  checked={enabled}
                  onCheckedChange={setEnabled}
                />
              </PreferenceRow>
              <PreferenceRow
                title="A preference with a deliberately long title"
                description="Long descriptions wrap while keeping the associated action reachable on smaller screens."
              >
                <Button variant="outline" size="sm">
                  Configure
                </Button>
              </PreferenceRow>
              <ActionBar>
                <Button variant="outline">Cancel</Button>
                <Button>Save changes</Button>
              </ActionBar>
              <DesktopNavButton active>
                <Folder className="size-4" />
                Active navigation item
              </DesktopNavButton>
              <DesktopNavButton disabled>
                <Folder className="size-4" />
                Unavailable navigation item
              </DesktopNavButton>
            </Section>
            <Section title="Feedback and empty states">
              <InlineMessage title="Conversation could not be saved." tone="error">
                Plugin lease failed (500).
              </InlineMessage>
              <InlineMessage
                actions={
                  <Button size="xs" variant="outline">
                    Locate
                  </Button>
                }
                title="crowdex is not available on this device."
                tone="warning"
              />
              <InlineMessage tone="success">Your changes were saved.</InlineMessage>
              <InlineMessage tone="info">The conversation continues in the cloud.</InlineMessage>
              <div className="flex items-center gap-3">
                {['relative h-7 w-5', 'relative size-9', 'relative h-10 min-w-0 flex-1'].map(
                  (size) => (
                    <div
                      className={`${size} shrink-0 overflow-hidden rounded-md border border-hairline`}
                      key={size}
                    >
                      <PluginFrameError
                        message="Error invoking remote method 'crowdex:invoke': Error: Plugin lease failed (500)."
                        name="Git status"
                        onRestart={() => undefined}
                      />
                    </div>
                  ),
                )}
              </div>
              <LoadingState>Loading projects…</LoadingState>
              <EmptyState
                icon={<Folder />}
                title="No projects yet"
                description="Create a project to start working with your agents."
                action={
                  <Button variant="outline">
                    <Plus />
                    New project
                  </Button>
                }
              />
            </Section>
            <Section
              title="Overlays and keyboard interaction"
              description="Open the dialog and its nested surfaces to inspect focus and stacking."
            >
              <OverlayExamples />
            </Section>
            <Section title="Typography and surfaces">
              <p className="type-caption">Label · 12</p>
              <p className="type-ui">Interface · 13</p>
              <p className="type-body">Body · 14</p>
              <p className="type-card-title">Title · 16</p>
              <p className="type-section-title">Section · 20</p>
              <p className="type-page-title">Page · 28</p>
              <code className="block text-code">const theme = 'crowdex';</code>
              <div className="grid grid-cols-2 gap-3">
                {SWATCHES.map(([name, className]) => (
                  <div key={name}>
                    <div className={`h-12 rounded-lg border border-border ${className}`} />
                    <p className="mt-1 text-caption text-muted-foreground">{name}</p>
                  </div>
                ))}
              </div>
            </Section>
          </div>
        </div>
      </main>
    </TooltipProvider>
  );
}
